import { LastActiveSource, USER_ROLE_LABELS, UserRole } from '../models/user';

/**
 * Presentation helpers for user rows, shared by the partner-details Users tab and the Usage page.
 *
 * These take primitives and minimal structural types rather than a concrete DTO: the two pages
 * deal in different response shapes (AdminUserDTO and UsageUserDTO), and typing against one of
 * them would leave the other working only by structural coincidence.
 */

const RELATIVE_TIME = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

/** Anything carrying a last-active reading, whichever endpoint it came from. */
export interface LastActiveBearer {
  lastActiveAt?: string | null;
  lastActiveSource?: LastActiveSource | null;
}

export function userRoleLabel(role?: UserRole | string | null): string {
  if (!role) {
    return 'No role';
  }
  // Curated label if we have one, otherwise humanize the raw enum name so new backend
  // roles need no hub change (e.g. TECH_MANAGER -> "Tech Manager"). Mirrors flagLabel().
  return USER_ROLE_LABELS[role as UserRole]
    || String(role).replace(/_/g, ' ')
                   .toLowerCase()
                   .replace(/\b\w/g, c => c.toUpperCase());
}

export function userRoleClass(role?: UserRole | string | null): string {
  return `role-${String(role ?? 'unknown').toLowerCase().replace(/_/g, '-')}`;
}

/**
 * Two different things can be switched off, and telling them apart matters when somebody works for
 * more than one organisation: the Keycloak account (realm-wide, so they cannot sign in at all) and
 * their membership of the tenant being listed (this organisation only, and they go on working
 * elsewhere). `membershipActive` is undefined for a membership that predates that distinction.
 */
export function userStatusLabel(enabled: boolean, membershipActive?: boolean | null): string {
  if (!enabled) {
    return 'Disabled';
  }
  return membershipActive === false ? 'Inactive here' : 'Enabled';
}

export function userStatusClass(enabled: boolean, membershipActive?: boolean | null): string {
  return enabled && membershipActive !== false ? 'status-active' : 'status-inactive';
}

/** "3 days ago" for a timestamp, "Never" when we have nothing on record. */
export function formatRelative(iso?: string | null): string {
  if (!iso) {
    return 'Never';
  }

  const then = new Date(iso).getTime();
  if (isNaN(then)) {
    return '—';
  }

  const diffSeconds = Math.round((then - Date.now()) / 1000); // negative = in the past
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31536000], ['month', 2592000], ['day', 86400],
    ['hour', 3600], ['minute', 60], ['second', 1]
  ];

  for (const [unit, seconds] of units) {
    if (Math.abs(diffSeconds) >= seconds || unit === 'second') {
      return RELATIVE_TIME.format(Math.round(diffSeconds / seconds), unit);
    }
  }
  return '—';
}

export function formatAbsolute(iso?: string | null): string {
  if (!iso) {
    return '';
  }
  const date = new Date(iso);
  return isNaN(date.getTime()) ? '' : date.toLocaleString();
}

export function lastActiveTooltip(user: LastActiveBearer): string {
  if (!user.lastActiveAt) {
    return 'No activity on record. Either this user has never signed in, or they last signed in '
      + 'before login logging was enabled in Keycloak.';
  }

  // A login event only dates the sign-in — it is the fallback for a user with no session left
  // to read, so say so rather than passing it off as activity.
  const source = user.lastActiveSource === LastActiveSource.ACTIVE_SESSION ? ' (last activity on an active session)'
    : user.lastActiveSource === LastActiveSource.OFFLINE_SESSION ? ' (last activity on an offline session)'
    : user.lastActiveSource === LastActiveSource.LOGIN_EVENT ? ' (sign-in time, from a Keycloak login event)'
    : '';

  return `${formatAbsolute(user.lastActiveAt)}${source}`;
}
